import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Calculator } from 'lucide-react';
import { Button } from '$lib/components/ui/Button';
import { AddressAutocomplete, type AutocompleteItem } from '$lib/components/estimate/AddressAutocomplete';
import { EstimateError, postEstimate, type EstimateResponse } from '$lib/api/estimate';
import { EstimateResult } from '$lib/components/estimate/EstimateResult';

export function EstimatePage() {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [selected, setSelected] = useState<AutocompleteItem | null>(null);

  const mutation = useMutation<EstimateResponse, EstimateError, void>({
    mutationFn: () =>
      postEstimate({
        address: selected
          ? `${selected.street_name} ${selected.house_number}`
          : address.trim(),
        postal_code: selected?.postal_code || null,
        floor: selected?.floor || null,
        door: selected?.door || null
      })
  });

  const handleChange = (v: string) => {
    setAddress(v);
    if (selected && v !== selected.label) {
      setSelected(null);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (address.trim().length < 2) return;
    mutation.mutate();
  };

  return (
    <div
      className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto"
      data-testid="estimate-page"
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-4 py-3">
        <Calculator size={16} className="text-accent" />
        <h1 className="text-md font-semibold text-fg">{t('estimate.title')}</h1>
        <span className="text-xs text-fg-muted">{t('estimate.subtitle')}</span>
      </header>

      <div className="flex-1 px-4 py-4">
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          <form
            onSubmit={submit}
            className="flex flex-col gap-3 rounded-sm border border-rule bg-surface p-3"
            data-testid="estimate-form"
          >
            <label className="flex flex-col gap-1">
              <span className="text-2xs uppercase tracking-wider text-fg-subtle">
                {t('estimate.fields.address')}
              </span>
              <AddressAutocomplete
                value={address}
                onChange={handleChange}
                onSelect={(item) => setSelected(item)}
                placeholder={t('estimate.fields.addressPlaceholder')}
                required
                minLength={2}
                testId="estimate-address-input"
                autoFocus
              />
            </label>

            {selected && (
              <div className="flex flex-wrap gap-2 text-2xs text-fg-muted" data-testid="estimate-selected-details">
                <span>{selected.postal_code} {selected.city}</span>
                {selected.floor && <span>· {t('estimate.fields.floor')}: {selected.floor}</span>}
                {selected.door && <span>· {t('estimate.fields.door')}: {selected.door}</span>}
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={mutation.isPending || address.trim().length < 2}
                data-testid="estimate-submit"
              >
                {mutation.isPending ? t('estimate.submitting') : t('estimate.submit')}
              </Button>
              <span className="text-2xs text-fg-muted">{t('estimate.helper')}</span>
            </div>
          </form>

          {mutation.isError && <ErrorState error={mutation.error} />}
          {mutation.isSuccess && <EstimateResult data={mutation.data} />}
        </div>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  error: EstimateError;
}
function ErrorState({ error }: ErrorStateProps) {
  const { t } = useTranslation();

  let titleKey = 'estimate.error.unknownTitle';
  let bodyKey = 'estimate.error.unknownBody';
  let action: { to: string; label: string } | null = null;

  if (error.reason === 'address_unresolved') {
    titleKey = 'estimate.error.unresolvedTitle';
    bodyKey = 'estimate.error.unresolvedBody';
  } else if (error.reason === 'no_apartment_at_dar') {
    titleKey = 'estimate.error.noApartmentTitle';
    bodyKey = 'estimate.error.noApartmentBody';
  } else if (error.reason === 'out_of_scope') {
    titleKey = 'estimate.error.outOfScopeTitle';
    bodyKey = 'estimate.error.outOfScopeBody';
    action = { to: '/admin/scope', label: t('estimate.error.openScope') };
  }

  return (
    <div
      role="alert"
      data-testid="estimate-error"
      data-reason={error.reason}
      className="flex flex-col gap-1 rounded-sm border border-warning/35 bg-warning/10 px-3 py-3"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-warning">
        <AlertTriangle size={14} />
        {t(titleKey)}
      </div>
      <p className="text-2xs text-fg-muted">{t(bodyKey)}</p>
      {action && (
        <Link
          to={action.to}
          className="mt-1 inline-block text-2xs text-accent hover:underline"
        >
          {action.label} →
        </Link>
      )}
    </div>
  );
}
